/**
 * Maps a local sketch document to relational sketch.* rows and back.
 *
 * The local model is one DOCUMENT per sketch; the database is relational
 * (projects → rooms → walls → wall_obstructions). This module is the whole
 * impedance match, kept pure so the round trip is unit-tested without a network
 * or a browser (ADR 0019). The sync client does the I/O; this does the shape.
 *
 * Invariants it must honour (they are DB CHECK constraints, not suggestions):
 *   - walls.length_units is NOT NULL and > 0. Local unmeasured walls carry no
 *     length, so we resolve one from the pixel geometry via pxPerUnit. On the
 *     way back, unmeasured walls drop the length again (it is derived).
 *   - walls.angle_deg ∈ the Cabinet Vision set. The local angleDeg already is.
 *   - wall_number is null for openings, set for walls (walls_number_matches_kind).
 *   - obstruction_type must be a seeded key (a foreign key).
 *
 * Fields the relational schema does not model — the Client/Opportunity/Space
 * identity, version + preferred flag, door height, the Wall 1 pin, helper-line
 * choices — ride in rooms.client_state (ADR 0019/0021). Geometry never does.
 */
import { fromCvAngle, median, toCvAngle } from '../geometry/index.js';

const num = (v) => (Number.isFinite(v) ? v : null);
const clean = (v) => String(v ?? '').trim();

/**
 * Pixels-per-unit implied by a document's MEASURED walls (median of pixel
 * length ÷ entered length). Null when nothing is measured. The median, not the
 * mean, so one mistyped length cannot skew the scale — the same choice the
 * closure solver makes.
 */
export function pxPerUnitOf(walls = []) {
  const ratios = [];
  for (const w of walls) {
    if (!w.isMeasured || !(w.lengthUnits > 0)) continue;
    const px = Math.hypot(w.end.x - w.start.x, w.end.y - w.start.y);
    if (px > 0) ratios.push(px / w.lengthUnits);
  }
  return ratios.length ? median(ratios) : null;
}

/** Resolved length in UNITS for a wall, always > 0 (a DB requirement). */
function wallLengthUnits(w, pxPerUnit) {
  if (w.isMeasured && w.lengthUnits > 0) return w.lengthUnits;
  const px = Math.hypot(w.end.x - w.start.x, w.end.y - w.start.y);
  const units = pxPerUnit ? px / pxPerUnit : px;
  return units > 0 ? units : 0.001; // never 0 — the CHECK forbids it
}

/**
 * A local sketch document → the rows that represent it.
 *
 * @param {object} doc a stored sketch document
 * @param {object} ctx
 * @param {string} ctx.ownerId    the signed-in user's id (RLS owner)
 * @param {string} ctx.projectId  deriveProjectId(owner, client, opportunity)
 * @param {string} [ctx.clientId] this device's id (provenance only)
 * @returns {{project:object, room:object, walls:object[], obstructions:object[]}}
 */
export function sketchToRows(doc, { ownerId, projectId, clientId = null } = {}) {
  const walls = doc.walls ?? [];
  const pxPerUnit = Number.isFinite(doc.scalePxPerUnit) && doc.scalePxPerUnit > 0
    ? doc.scalePxPerUnit
    : pxPerUnitOf(walls);

  const project = {
    id: projectId,
    owner_id: ownerId,
    name: [clean(doc.client), clean(doc.opportunity)].filter(Boolean).join(' — ') || 'Untitled project',
    // The local "opportunity" is a free-text label, NOT a Salesforce id, and the
    // column has a strict CHECK — so it stays null until a real SF id is wired.
    salesforce_opportunity_id: null,
    client_id: clientId,
  };

  const room = {
    id: doc.id,
    project_id: projectId,
    name: clean(doc.space) || 'Room',
    unit_of_measure: 'in',
    ceiling_height_units: num(doc.ceilingHeightUnits),
    client_id: clientId,
    // Everything the relational schema does not model, kept lossless.
    client_state: {
      client: clean(doc.client),
      opportunity: clean(doc.opportunity),
      space: clean(doc.space),
      version: clean(doc.version) || 'Version 1',
      isPreferred: doc.isPreferred === true,
      // A reusable template rides in client_state like any other non-relational
      // field. Its room id is unique (doc.id), so a blank-identity template
      // never collides with another on the room-as-sync-unit key (ADR 0029).
      isTemplate: doc.isTemplate === true,
      templateName: clean(doc.templateName),
      templateRank: doc.isTemplate === true && Number.isInteger(doc.templateRank) && doc.templateRank >= 0 ? doc.templateRank : null,
      doorHeightUnits: num(doc.doorHeightUnits),
      snapClosed: doc.snapClosed !== false,
      closedPerimeter: doc.closedPerimeter === true,
      wallOneOverrideId: doc.wallOneOverrideId ?? null,
      scalePxPerUnit: Number.isFinite(doc.scalePxPerUnit) && doc.scalePxPerUnit > 0
        ? doc.scalePxPerUnit : null,
      // The relational wall row's is_measured flag means "fixed for solving",
      // not provenance. Keep per-value copied/measured state beside the room so
      // sync cannot turn inherited measurements into locally-captured ones.
      wallMeasurementStates: Object.fromEntries(walls
        .filter((wall) => wall?.measurementState === 'inherited' || wall?.measurementState === 'measured')
        .map((wall) => [wall.id, wall.measurementState])),
      helperLineOverrides: doc.helperLineOverrides && typeof doc.helperLineOverrides === 'object' ? doc.helperLineOverrides : {},
      // Up to 3 custom helper lines since 2026-07-28 (was a single line).
      customHelperLines: Array.isArray(doc.customHelperLines) ? doc.customHelperLines : [],
      // Freehand design-sketch strokes ride along so annotations back up and
      // travel between devices with their version (ADR 0023).
      annotations: Array.isArray(doc.annotations) ? doc.annotations : [],
      // Depth helper lines and the grid toggle (ADR 0024).
      planHelperLines: Array.isArray(doc.planHelperLines) ? doc.planHelperLines : [],
      planHelperLinesVisible: doc.planHelperLinesVisible !== false,
      // Measurement-origin preference has no relational column, so preserve it
      // losslessly in room client_state while geometry remains in wall rows.
      obstructionReferenceEdges: Object.fromEntries((doc.obstructions ?? [])
        .filter((o) => o && typeof o.id === 'string' && o.referenceEdge === 'right')
        .map((o) => [o.id, 'right'])),
      openingDetails: Object.fromEntries((doc.obstructions ?? [])
        .filter((o) => o?.opening && typeof o.opening === 'object')
        .map((o) => [o.id, o.opening])),
      ceilingOpenings: Array.isArray(doc.ceilingOpenings) ? doc.ceilingOpenings : [],
      gridVisible: doc.gridVisible === true,
      // Elevation dimension toggles (ADR 0061 item E).
      shelfSpacingDimsVisible: doc.shelfSpacingDimsVisible === true,
      drawerFaceDimsVisible: doc.drawerFaceDimsVisible === true,
      storageSummaryVisible: doc.storageSummaryVisible !== false,
      // Interior/partition walls (ADR 0032) — anchored to a host wall, so they
      // ride in client_state like the other non-relational geometry helpers.
      partitions: Array.isArray(doc.partitions) ? doc.partitions : [],
      // Standard section-type labels anchored to a wall (ADR 0034).
      wallLabels: Array.isArray(doc.wallLabels) ? doc.wallLabels : [],
      // Wall sections (ADR 0037): typed cabinetry units with a layout solver
      // client-side; only the inputs travel, computed openings are derived.
      // Sections travel WHOLE, so per-section fields — including the composed
      // `interior` (element `accessories` included) and `doors` (ADR 0054) —
      // ride inside them; hydrateSketch re-validates field by field on load.
      sections: Array.isArray(doc.sections) ? doc.sections : [],
      // Shared-space split (A/B): toggle + person names.
      sharedSpace: doc.sharedSpace && typeof doc.sharedSpace === 'object' ? doc.sharedSpace : { enabled: false },
      // Space-level section height override.
      cornerHeights: doc.cornerHeights && typeof doc.cornerHeights === 'object' ? doc.cornerHeights : {},
      chainStartHeightUnits: Number.isFinite(doc.chainStartHeightUnits) && doc.chainStartHeightUnits > 0
        ? doc.chainStartHeightUnits : null,
      wallHeightBreaks: doc.wallHeightBreaks && typeof doc.wallHeightBreaks === 'object' ? doc.wallHeightBreaks : {},
      storageNeeds: doc.storageNeeds && typeof doc.storageNeeds === 'object' ? doc.storageNeeds : {},
      storageNeedsSource: doc.storageNeedsSource && typeof doc.storageNeedsSource === 'object' ? doc.storageNeedsSource : null,
      storageCompareTo: ({ current: 'current', has: 'current', need: 'need', wants: 'need' })[doc.storageCompareTo] ?? 'need',
      spaceHeightUnits: Number.isFinite(doc.spaceHeightUnits) && doc.spaceHeightUnits > 0 ? doc.spaceHeightUnits : null,
      platformHeightUnits: Number.isFinite(doc.platformHeightUnits) && doc.platformHeightUnits > 0 ? doc.platformHeightUnits : 0,
      // Vertical stacking (ADR 0041): space countertop + to-the-ceiling mode.
      countertopThicknessUnits: Number.isFinite(doc.countertopThicknessUnits) && doc.countertopThicknessUnits > 0
        ? doc.countertopThicknessUnits : 0,
      toTargetHeight: doc.toTargetHeight === true || doc.toCeiling === true,
      stackTargetHeightUnits: Number.isFinite(doc.stackTargetHeightUnits) && doc.stackTargetHeightUnits > 0
        ? doc.stackTargetHeightUnits : null,
      // Per-wall target overrides (owner, 2026-08-04); sparse, absent = follows
      // the space.
      wallStackTargets: doc.wallStackTargets ?? {},
      crownAllowanceUnits: Number.isFinite(doc.crownAllowanceUnits) && doc.crownAllowanceUnits >= 0
        ? doc.crownAllowanceUnits : null,
      // Per-space trim-height overrides (owner, 2026-08-28); null = rule.
      crownMouldingHeightUnits: Number.isFinite(doc.crownMouldingHeightUnits) && doc.crownMouldingHeightUnits > 0
        ? doc.crownMouldingHeightUnits : null,
      baseTrimHeightUnits: Number.isFinite(doc.baseTrimHeightUnits) && doc.baseTrimHeightUnits > 0
        ? doc.baseTrimHeightUnits : null,
      // Space pricing (ADR 0058): level + countertop picks and the stamped
      // low/high totals ride in client_state like the other non-relational
      // fields; hydrateSketch re-validates on the way back in. This is what
      // the space's PREFERRED version will hand the ballpark (ADR 0021 — the
      // cross-repo transport is a follow-up ADR).
      pricing: doc.pricing && typeof doc.pricing === 'object' ? doc.pricing : null,
      // Basic mode (ADR 0059): mode, space type, span bounds and the staged
      // bucket cards ride whole; hydrateSketch re-validates on the way back.
      basic: doc.basic && typeof doc.basic === 'object' ? doc.basic : null,
    },
  };

  const wallRows = walls.map((w) => {
    const isOpening = (w.segmentKind ?? 'wall') === 'opening';
    return {
      id: w.id,
      room_id: doc.id,
      sequence_index: w.sequenceIndex,
      wall_number: isOpening ? null : w.wallNumber,
      segment_kind: w.segmentKind ?? 'wall',
      angle_deg: w.angleDeg,
      length_units: wallLengthUnits(w, pxPerUnit),
      is_measured: !!w.isMeasured,
      start_x: num(w.start.x),
      start_y: num(w.start.y),
      end_x: num(w.end.x),
      end_y: num(w.end.y),
      height_units: num(w.heightUnits),
      include_in_elevation: w.includeInElevation !== false,
      // Only a POSITIVE override is meaningful; anything else (absent, 0,
      // negative) means "use the business-rule default", stored as NULL. The
      // DB CHECK is `thickness_units IS NULL OR > 0`, so this also defends
      // against a poisoned 0 sneaking back in and failing every push (ADR 0030).
      thickness_units: Number.isFinite(w.thicknessUnits) && w.thicknessUnits > 0 ? w.thicknessUnits : null,
      client_id: clientId,
    };
  });

  const wallIds = new Set(wallRows.map((w) => w.id));
  const obstructionRows = (doc.obstructions ?? [])
    .filter((o) => wallIds.has(o.wallId))
    .map((o) => ({
      id: o.id,
      wall_id: o.wallId,
      obstruction_type: o.type ?? 'custom',
      offset_along_wall: Math.max(0, num(o.offsetAlongWall) ?? 0),
      offset_from_floor: Math.max(0, num(o.offsetFromFloor) ?? 0),
      width_units: o.widthUnits > 0 ? o.widthUnits : 0.001,
      height_units: o.heightUnits > 0 ? o.heightUnits : 0.001,
      depth_units: num(o.depthUnits),
      face_offset_units: num(o.faceOffsetUnits) ?? 0,
      // Existing cloud schema has no reference-edge column; the local room doc
      // retains this capture-origin metadata alongside canonical geometry.
      label: o.label ?? null,
      locked: o.locked === true,
      client_id: clientId,
    }));

  return { project, room, walls: wallRows, obstructions: obstructionRows };
}

/**
 * The inverse: rows for ONE room → a local sketch document, shaped exactly like
 * serializeSketch() output so hydrateSketch() accepts it unchanged.
 *
 * @param {object} args
 * @param {object} args.room
 * @param {object[]} args.walls        walls for this room
 * @param {object[]} args.obstructions obstructions for this room's walls
 * @returns {object} a sketch document
 */
export function rowsToSketch({ room, walls = [], obstructions = [] }) {
  const cs = room.client_state && typeof room.client_state === 'object' ? room.client_state : {};

  const wallDocs = walls
    .slice()
    .sort((a, b) => a.sequence_index - b.sequence_index)
    .map((w) => ({
      id: w.id,
      segmentKind: w.segment_kind ?? 'wall',
      angleDeg: w.angle_deg,
      // internalAngle is derived (not stored): reconstruct from the CV angle.
      internalAngle: fromCvAngle(w.angle_deg),
      start: { x: w.start_x, y: w.start_y },
      end: { x: w.end_x, y: w.end_y },
      isMeasured: !!w.is_measured,
      measurementState: ['inherited', 'measured'].includes(cs.wallMeasurementStates?.[w.id])
        ? cs.wallMeasurementStates[w.id] : null,
      // Length is authoritative only for measured walls; derived otherwise.
      lengthUnits: w.is_measured ? Number(w.length_units) : null,
      sequenceIndex: w.sequence_index,
      wallNumber: w.wall_number ?? null,
      includeInElevation: w.include_in_elevation !== false,
      // NULL means "no override" — must stay null. `Number(null)` is 0, which
      // both renders a 0-thickness wall AND fails the push CHECK on the next
      // sync; guard exactly like depth_units below (ADR 0030).
      thicknessUnits: w.thickness_units == null ? null : num(Number(w.thickness_units)),
    }));

  const wallIds = new Set(wallDocs.map((w) => w.id));
  const obstructionDocs = obstructions
    .filter((o) => wallIds.has(o.wall_id))
    .map((o) => ({
      id: o.id,
      wallId: o.wall_id,
      type: o.obstruction_type ?? 'custom',
      label: o.label ?? '',
      offsetAlongWall: Number(o.offset_along_wall),
      offsetFromFloor: Number(o.offset_from_floor),
      widthUnits: Number(o.width_units),
      heightUnits: Number(o.height_units),
      depthUnits: o.depth_units == null ? null : Number(o.depth_units),
      faceOffsetUnits: Number.isFinite(Number(o.face_offset_units)) ? Number(o.face_offset_units) : 0,
      referenceEdge: cs.obstructionReferenceEdges?.[o.id] === 'right' ? 'right' : 'left',
      opening: cs.openingDetails?.[o.id] && typeof cs.openingDetails[o.id] === 'object'
        ? cs.openingDetails[o.id] : null,
      locked: o.locked === true,
    }));

  return {
    schemaVersion: 1,
    id: room.id,
    client: clean(cs.client),
    opportunity: clean(cs.opportunity),
    space: clean(cs.space) || clean(room.name),
    version: clean(cs.version) || 'Version 1',
    isPreferred: cs.isPreferred === true,
    isTemplate: cs.isTemplate === true,
    templateName: clean(cs.templateName),
    templateRank: cs.isTemplate === true && Number.isInteger(cs.templateRank) && cs.templateRank >= 0 ? cs.templateRank : null,
    updatedAt: room.updated_at ?? new Date().toISOString(),
    selectedWallId: null,
    snapClosed: cs.snapClosed !== false,
    closedPerimeter: cs.closedPerimeter === true,
    wallOneOverrideId: cs.wallOneOverrideId ?? null,
    scalePxPerUnit: Number.isFinite(cs.scalePxPerUnit) && cs.scalePxPerUnit > 0
      ? cs.scalePxPerUnit : null,
    ceilingHeightUnits: num(room.ceiling_height_units == null ? null : Number(room.ceiling_height_units)),
    doorHeightUnits: num(cs.doorHeightUnits),
    ceilingOpenings: Array.isArray(cs.ceilingOpenings) ? cs.ceilingOpenings : [],
    helperLineOverrides: cs.helperLineOverrides && typeof cs.helperLineOverrides === 'object' ? cs.helperLineOverrides : {},
    customHelperLines: Array.isArray(cs.customHelperLines)
      ? cs.customHelperLines
      : (num(cs.customHelperLineUnits)
        ? [{ units: num(cs.customHelperLineUnits), visible: cs.customHelperVisible !== false }]
        : []),
    annotations: Array.isArray(cs.annotations) ? cs.annotations : [],
    planHelperLines: Array.isArray(cs.planHelperLines) ? cs.planHelperLines : [],
    planHelperLinesVisible: cs.planHelperLinesVisible !== false,
    gridVisible: cs.gridVisible === true,
    shelfSpacingDimsVisible: cs.shelfSpacingDimsVisible === true,
    drawerFaceDimsVisible: cs.drawerFaceDimsVisible === true,
    storageSummaryVisible: cs.storageSummaryVisible !== false,
    partitions: Array.isArray(cs.partitions) ? cs.partitions : [],
    wallLabels: Array.isArray(cs.wallLabels) ? cs.wallLabels : [],
    // Whole sections back out of client_state — `interior`/`doors` (ADR 0054)
    // included; hydrateSketch is the validating gate on the way into the model.
    sections: Array.isArray(cs.sections) ? cs.sections : [],
    sharedSpace: cs.sharedSpace && typeof cs.sharedSpace === 'object' ? cs.sharedSpace : { enabled: false },
    cornerHeights: cs.cornerHeights && typeof cs.cornerHeights === 'object' ? cs.cornerHeights : {},
    chainStartHeightUnits: Number.isFinite(cs.chainStartHeightUnits) && cs.chainStartHeightUnits > 0
      ? cs.chainStartHeightUnits : null,
    wallHeightBreaks: cs.wallHeightBreaks && typeof cs.wallHeightBreaks === 'object' ? cs.wallHeightBreaks : {},
    storageNeeds: cs.storageNeeds && typeof cs.storageNeeds === 'object' ? cs.storageNeeds : {},
    storageNeedsSource: cs.storageNeedsSource && typeof cs.storageNeedsSource === 'object' ? cs.storageNeedsSource : null,
    storageCompareTo: ({ current: 'current', has: 'current', need: 'need', wants: 'need' })[cs.storageCompareTo] ?? 'need',
    spaceHeightUnits: Number.isFinite(cs.spaceHeightUnits) && cs.spaceHeightUnits > 0 ? cs.spaceHeightUnits : null,
    platformHeightUnits: Number.isFinite(cs.platformHeightUnits) && cs.platformHeightUnits > 0 ? cs.platformHeightUnits : 0,
    countertopThicknessUnits: Number.isFinite(cs.countertopThicknessUnits) && cs.countertopThicknessUnits > 0
      ? cs.countertopThicknessUnits : 0,
    toTargetHeight: cs.toTargetHeight === true || cs.toCeiling === true,
    stackTargetHeightUnits: Number.isFinite(cs.stackTargetHeightUnits) && cs.stackTargetHeightUnits > 0
      ? cs.stackTargetHeightUnits : null,
    wallStackTargets: cs.wallStackTargets ?? {},
    crownAllowanceUnits: Number.isFinite(cs.crownAllowanceUnits) && cs.crownAllowanceUnits >= 0
      ? cs.crownAllowanceUnits : null,
    crownMouldingHeightUnits: Number.isFinite(cs.crownMouldingHeightUnits) && cs.crownMouldingHeightUnits > 0
      ? cs.crownMouldingHeightUnits : null,
    baseTrimHeightUnits: Number.isFinite(cs.baseTrimHeightUnits) && cs.baseTrimHeightUnits > 0
      ? cs.baseTrimHeightUnits : null,
    // Space pricing (ADR 0058); hydrateSketch validates field by field.
    pricing: cs.pricing && typeof cs.pricing === 'object' ? cs.pricing : null,
    // Basic mode (ADR 0059); hydrateSketch validates field by field.
    basic: cs.basic && typeof cs.basic === 'object' ? cs.basic : null,
    obstructions: obstructionDocs,
    walls: wallDocs,
  };
}

// Re-exported so callers building the CV angle for a synthetic wall stay
// consistent with the geometry engine.
export { toCvAngle };
